import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';
import {
  ISequenceStep,
  DEFAULT_AI_LANGUAGE,
  EMAIL_PREFERENCE_OPTIONS,
  IF_NO_EMAIL_OPTIONS,
  EmailPreference,
  IfNoEmail,
  ISenderIdentity,
} from '../../../../interfaces/models/campaign';
import {
  CAMPAIGN_TEMPLATE_VARIABLES,
  CampaignVariableKey,
  ICampaignVariableFallbacks,
  extractVariablesFromText,
  unrecognizedVariableTokens,
  variableToken,
  renderCampaignTemplate,
} from '../../../../utils/campaignTemplateVariables';
import VariableFallbackPopover from './VariableFallbackPopover';
import { CampaignVariableWarnBanner } from './CampaignVariableWarnModal';
import CampaignComposeActions, { CampaignStepTools, UndoIcon } from './CampaignComposeActions';
import CampaignGeneratePopup from './CampaignGeneratePopup';
import InfoTip from './InfoTip';
import LinkUrlDialog from './LinkUrlDialog';
import useReplaceUndo from './useReplaceUndo';
import campaignSelectStyle from './campaignSelectStyle';
import StyledModal from '../../../../components/modal/styledModals/StyledModal';
import SendingAccountsSection from '../../sections/SendingAccountsSection';
import { useCampaignsRequest } from '../../../../services/apis/campaigns/useCampaignsRequest';
import { campaignMobile } from '../../utils/campaignBreakpoints';

/**
 * The email step's composer.
 *
 * Split out of CampaignSequenceBuilder, which was already past 1600 lines: an email
 * step carries a subject, a body, an address preference and a no-address fallback,
 * and inlining all of that made the surrounding switch unreadable.
 *
 * There is one field for the subject and one for the body — no mode tabs. You either
 * write the email or describe what you want, and "Generate message" hands back a draft
 * in a dialog; taking it replaces what is in the field. What you can read here is
 * therefore what every lead receives, with their own {{variables}} filled in at send
 * time — the same contract as a LinkedIn DM or a post comment.
 *
 * A step still stored in the old per-lead mode ('ai') is left exactly as it is until
 * someone edits it: the instruction shows in the field under a warning strip, and the
 * first edit — or an accepted draft — is what flips it to a fixed body. Rewriting it on
 * open would have quietly turned the instruction itself into the email being sent.
 */

/** Lead used for the "Preview:" line under the subject. */
const SAMPLE_LEAD = {
  firstName: 'Alex',
  lastName: 'Chen',
  name: 'Alex Chen',
  company: 'Acme',
  title: 'VP of Sales',
};

const IMAGE_MAX_BYTES = 2 * 1024 * 1024;
const IMAGE_TYPES = 'image/png,image/jpeg,image/gif,image/webp';

interface Props {
  step: ISequenceStep;
  onChange: (patch: Partial<ISequenceStep>) => void;
  fallbacks: ICampaignVariableFallbacks;
  onFallbackChange: (key: CampaignVariableKey, value: string, saveAsUserDefault?: boolean) => void;
  /** False for a follow-up that replies in-thread — it inherits the original subject. */
  needsSubject: boolean;
  campaignDescription?: string;
  /** Senders this campaign sends as — each appends its own signature. */
  senderIdentities?: ISenderIdentity[];
  /**
   * Campaign-level footer settings. Scoped to the campaign rather than the step: one
   * postal address belongs to the whole send, not to each email in the sequence.
   */
  emailConfig?: { physicalAddress?: string; footer?: string };
  onEmailConfigChange?: (patch: { physicalAddress?: string; footer?: string }) => void;
  /** Paint required-but-empty fields as invalid — see the builder's prop of this name. */
  showErrors?: boolean;
  readOnly?: boolean;
  /** Paused campaign: subject and body stay editable, delivery rules do not. */
  lockDeliveryOptions?: boolean;
}

const CampaignEmailComposer: React.FC<Props> = ({
  step, onChange, fallbacks, onFallbackChange, needsSubject, campaignDescription,
  senderIdentities = [], emailConfig, onEmailConfigChange, showErrors = false, readOnly,
  lockDeliveryOptions = false,
}) => {
  const { uploadEmailImage } = useCampaignsRequest();

  const [popover, setPopover] = useState<
    { key: CampaignVariableKey; anchorRect: DOMRect; pendingInsert: boolean } | null
  >(null);
  const [varMenuFor, setVarMenuFor] = useState<'subject' | 'body' | null>(null);
  /** Which dialog is open: a generated draft, or a preview of what is written. */
  const [generateFor, setGenerateFor] = useState<'body' | 'preview' | null>(null);
  /** Sending-account settings, opened over the campaign rather than navigated to. */
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [imageError, setImageError] = useState('');

  const bodyRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const savedRangeRef = useRef<Range | null>(null);
  /** Bumped when the body is replaced from outside the editor, never on typing. */
  const [bodyRevision, setBodyRevision] = useState(0);

  /*
    A step saved before the tabs went away. Its text lives in the *AiPrompt fields and
    is an instruction, not an email — it is still shown so the work is not lost, and
    the step is not rewritten until the user actually changes something.
  */
  const legacyBody = step.emailBodyMode === 'ai' && !(step.emailBodyTemplate ?? '').trim();
  const legacySubject = step.emailSubjectMode === 'ai' && !(step.emailSubjectTemplate ?? '').trim();

  const subjectValue = (step.emailSubjectTemplate ?? '')
    || (legacySubject ? (step.emailSubjectAiPrompt ?? '') : '');
  const bodyValue = (step.emailBodyTemplate ?? '')
    || (legacyBody ? textToHtml(step.emailBodyAiPrompt ?? '') : '');

  /** Read by readBody, which runs from DOM events and would otherwise close over a stale value. */
  const bodyValueRef = useRef(bodyValue);
  bodyValueRef.current = bodyValue;

  /*
    Every write from this composer clears finalContent. The executor checks that field
    before anything else, so a value frozen by the old AI flow would keep going out
    while the editor showed the body the user had just written.
  */
  const writeSubject = useCallback((text: string) => {
    onChange({ emailSubjectTemplate: text, emailSubjectMode: 'template' });
  }, [onChange]);

  // ── Rich-text body ──────────────────────────────────────────────────────────

  // Write into the contenteditable only when the value came from somewhere other than
  // typing in it. Assigning innerHTML every render would drop the caret to the start
  // of the body on each keystroke.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (el.innerHTML !== bodyValue) el.innerHTML = bodyValue;
    // Deliberately not keyed on bodyValue — see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyRevision]);

  /*
    onBlur fires this too, so it must be inert when nothing was actually typed.
    Writing unconditionally meant that on a live campaign still in the old per-lead
    mode, merely clicking into the body and back out flipped the step to a fixed
    email whose text was the instruction — a send-behaviour change nobody asked for,
    from a stray click.
  */
  const readBody = useCallback(() => {
    const el = bodyRef.current;
    if (!el || el.innerHTML === bodyValueRef.current) return;
    onChange({
      emailBodyTemplate: el.innerHTML,
      emailBodyHtml: true,
      emailBodyMode: 'template',
      finalContent: '',
    });
  }, [onChange]);

  /** Replace the whole body — from a generated draft or a template library entry. */
  const replaceBody = useCallback((html: string) => {
    onChange({
      emailBodyTemplate: html,
      emailBodyHtml: true,
      emailBodyMode: 'template',
      finalContent: '',
    });
    setBodyRevision(r => r + 1);
  }, [onChange]);

  /*
    One step back for the writes above. A contenteditable's native undo stack does not
    see an innerHTML assignment, so before this there was no way at all to recover a
    body that Use Template had overwritten.

    Subject AND body, as one unit. "Generate message" writes both — the subject it hands
    back is written FOR that body — so undoing only the body left a subject describing an
    email that was no longer there. They are replaced together, so they come back
    together; a template, which only carries a body, simply leaves the subject alone on
    the way there and on the way back.
  */
  const draft = useMemo(
    () => ({ subject: subjectValue, body: bodyValue }),
    [subjectValue, bodyValue],
  );

  const writeDraft = useCallback((next: { subject: string; body: string }) => {
    if (next.subject !== subjectValue) writeSubject(next.subject);
    if (next.body !== bodyValue) replaceBody(next.body);
  }, [subjectValue, bodyValue, writeSubject, replaceBody]);

  const bodyUndo = useReplaceUndo(
    draft,
    writeDraft,
    (a, b) => a.subject === b.subject && a.body === b.body,
  );

  /** Remember where the caret was, so a toolbar click can put things back there. */
  const rememberCaret = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && bodyRef.current?.contains(sel.anchorNode)) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  }, []);

  const restoreCaret = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.focus();
    const range = savedRangeRef.current;
    if (!range) return;
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, []);

  /**
   * execCommand is formally deprecated, but it remains the only thing every browser
   * implements for contenteditable formatting, and replacing it means shipping an
   * editor framework for a five-button toolbar.
   */
  const exec = useCallback((command: string, arg?: string) => {
    restoreCaret();
    document.execCommand(command, false, arg);
    readBody();
  }, [restoreCaret, readBody]);

  const insertHtmlAtCaret = useCallback((html: string) => {
    restoreCaret();
    document.execCommand('insertHTML', false, html);
    readBody();
  }, [restoreCaret, readBody]);

  /** The Link button asks in our own dialog, not window.prompt — see LinkUrlDialog. */
  const [linkOpen, setLinkOpen] = useState(false);

  const addLink = useCallback(() => {
    // The dialog takes focus; this is where the link goes once it hands it back.
    rememberCaret();
    setLinkOpen(true);
  }, [rememberCaret]);

  // The address arrives already limited to http(s) — see normalizeLinkUrl.
  const applyLink = useCallback((url: string) => {
    setLinkOpen(false);
    exec('createLink', url);
  }, [exec]);

  const pickImage = useCallback(async (file: File) => {
    if (file.size > IMAGE_MAX_BYTES) { setImageError('That image is over 2MB.'); return; }
    setImageBusy(true);
    setImageError('');
    const res: any = await uploadEmailImage(file);
    setImageBusy(false);

    const url = res?.data?.url ?? res?.url;
    if (!url) { setImageError(res?.message || 'Could not upload that image.'); return; }
    insertHtmlAtCaret(`<img src="${url}" style="max-width:100%;height:auto" />`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insertHtmlAtCaret]);

  // ── Variables ───────────────────────────────────────────────────────────────

  const insertVariable = useCallback((key: CampaignVariableKey, target: 'subject' | 'body') => {
    const token = variableToken(key);
    setVarMenuFor(null);

    if (target === 'subject') { writeSubject(`${subjectValue}${token}`); return; }
    insertHtmlAtCaret(token);
  }, [subjectValue, writeSubject, insertHtmlAtCaret]);

  /** Places the sign-off. Substituted with the sender's own signature at send time. */
  const insertSignature = useCallback(() => {
    setVarMenuFor(null);
    insertHtmlAtCaret('{{signature}}');
  }, [insertHtmlAtCaret]);

  const usedVariables = useMemo(
    () => extractVariablesFromText(`${subjectValue}\n${bodyValue}`),
    [subjectValue, bodyValue],
  );

  // The two dropdowns speak for themselves; their hints were removed from under them.

  // Every sender the campaign draws from gets its own signature appended, so all of
  // them are listed rather than just the first — with two senders and one signature
  // between them, showing one would hide exactly the case worth seeing.
  const signers = senderIdentities.map(i => ({
    id: i._id,
    name: i.name || i.linkedin?.name || i.linkedin?.email || 'Unnamed sender',
    html: i.signature?.enabled === false ? '' : (i.signature?.html || ''),
  }));

  /** Whether {{signature}} has anything to resolve to for at least one sender. */
  const hasAnySignature = signers.some(s => !!s.html);

  /*
    Who the preview is drawn as. The first sender, because a preview has to pick one —
    with several the subline says so, so it never reads as "this is the only sender".
  */
  const previewSender = useMemo(() => {
    const first = senderIdentities[0];
    if (!first) return undefined;
    const name = first.name || first.linkedin?.name || 'Sender';
    const address = first.mail?.address || '';
    const extra = senderIdentities.length > 1
      ? ` · 1 of ${senderIdentities.length} senders`
      : '';
    return {
      name,
      subline: `${address || 'No mailbox connected'}${extra}`,
      signature: first.signature?.enabled === false ? '' : (first.signature?.html || ''),
    };
  }, [senderIdentities]);


  const subjectPreview = useMemo(
    () => renderCampaignTemplate(subjectValue, SAMPLE_LEAD, '', fallbacks),
    [subjectValue, fallbacks],
  );

  // Characters a recipient actually sees — tags would otherwise inflate the count
  // into the thousands the moment anything is formatted.
  const bodyChars = useMemo(
    () => bodyValue.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').length,
    [bodyValue],
  );

  /** What "Generate" is given to work from — the markup would only confuse the model. */
  const bodyPlainText = useMemo(() => htmlToText(bodyValue), [bodyValue]);
  const [badTokens, setBadTokens] = useState<string[]>([]);
  useEffect(() => {
    const timer = window.setTimeout(
      () => setBadTokens(unrecognizedVariableTokens(`${subjectValue}\n${bodyPlainText}`, ['signature'])),
      500,
    );
    return () => window.clearTimeout(timer);
  }, [subjectValue, bodyPlainText]);

  const bodyEmpty = !bodyValue.replace(/<[^>]*>/g, '').trim() && !/<img/i.test(bodyValue);

  if (readOnly) return null;

  return (
    <Wrap>
      <CampaignStepTools
        language={step.aiLanguage || DEFAULT_AI_LANGUAGE}
        onLanguageChange={lang => onChange({ aiLanguage: lang })}
        languageStepType="email"
        onPreview={() => setGenerateFor('preview')}
        previewDisabled={bodyEmpty}
        onOpenSettings={() => setSettingsOpen(true)}
      />

      {/* No "this step still writes per lead" strip. It keyed off empty template
          fields, so it also greeted anyone opening a perfectly ordinary step whose
          subject and body simply had not been written yet. */}

      {needsSubject && (
        <Field>
          {/* No generate of its own. "Generate message" below writes the subject with
              the body, from the body — asking for one separately produced a line
              describing an email that had not been written yet. */}
          <FieldHead>
            <FieldLabel>Subject <ReqMark>*</ReqMark></FieldLabel>
          </FieldHead>
          <InputRow>
            <SubjectInput
              $invalid={showErrors && !subjectValue.trim()}
              value={subjectValue}
              placeholder="Enter a custom subject line for this email"
              /*
                Password managers were offering to fill this.

                A bare <input> with no name and no autocomplete is exactly what LastPass,
                1Password and friends treat as a candidate field, and they draw their icon
                inside it. `name` and `autoComplete` tell the browser what this is;
                data-lpignore and data-1p-ignore are the two managers' own opt-outs, which
                they honour where the standard attributes alone do not.
              */
              name="campaign-email-subject"
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              onChange={e => writeSubject(e.target.value)}
              /* Ctrl+Z from the subject undoes the same replacement as from the body —
                 Generate wrote both, so either field is a fair place to take it back. */
              onKeyDown={bodyUndo.onKeyDown}
            />
            <InputVarBtn
              type="button"
              title="Insert variable"
              onClick={() => setVarMenuFor(varMenuFor === 'subject' ? null : 'subject')}
            >
              {'{ }'}
            </InputVarBtn>
            {/*
              Beside the button, not inside it. Inside, the menu inherited the button's
              stacking context — and the click-catcher that closes menus sits above that
              context, so every click on a variable landed on the catcher instead: the
              menu closed and nothing was inserted. It was also a button nested in a
              button, which browsers do not all deliver clicks through.
            */}
            {varMenuFor === 'subject' && (
              <VarMenu $right>
                {CAMPAIGN_TEMPLATE_VARIABLES.map(v => (
                  <VarItem key={v.key} type="button" onClick={() => insertVariable(v.key, 'subject')}>
                    {v.token}
                  </VarItem>
                ))}
              </VarMenu>
            )}
          </InputRow>

          {subjectValue.trim() && <PreviewLine>Preview: {subjectPreview}</PreviewLine>}
        </Field>
      )}

      <Field>
        <FieldHead>
          <FieldLabel>Body <ReqMark>*</ReqMark></FieldLabel>
          <FieldHeadSpacer />
          <Chars>{bodyChars} characters</Chars>
        </FieldHead>

        {/* Editor first, toolbar under it — the controls stay next to the compose
            actions instead of being separated from them by the whole body. */}
        <EditorShell $invalid={showErrors && bodyEmpty}>
          <Editable
            ref={bodyRef}
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label="Email body"
            $empty={bodyEmpty}
            data-placeholder="Write your email, or describe what you want AI to generate"
            onInput={readBody}
            onBlur={() => { rememberCaret(); readBody(); }}
            onKeyDown={bodyUndo.onKeyDown}
            onKeyUp={rememberCaret}
            onMouseUp={rememberCaret}
          />

          <Toolbar onMouseDown={e => { e.preventDefault(); rememberCaret(); }}>
            <TBtn type="button" title="Bold" onClick={() => exec('bold')}><b>B</b></TBtn>
            <TBtn type="button" title="Italic" onClick={() => exec('italic')}><i>I</i></TBtn>
            <TBtn type="button" title="Underline" onClick={() => exec('underline')}><u>U</u></TBtn>
            <TSep />
            <TBtn type="button" title="Bulleted list" onClick={() => exec('insertUnorderedList')}><ListIcon /></TBtn>
            <TBtn type="button" title="Numbered list" onClick={() => exec('insertOrderedList')}><OrderedListIcon /></TBtn>
            <TSep />
            <TBtn type="button" title="Link" onClick={addLink}><LinkIcon /></TBtn>
            <TBtn type="button" title="Image" disabled={imageBusy} onClick={() => { rememberCaret(); fileRef.current?.click(); }}>
              <ImageIcon />
            </TBtn>
            <TSep />
            <TBtn type="button" title="Clear body" disabled={bodyEmpty} onClick={() => bodyUndo.replace({ subject: subjectValue, body: '' })}>
              <ClearIcon />
            </TBtn>
            {/* Beside the other things that act on the text, not down in the row of
                actions — undo is what takes those back. */}
            <TBtn
              type="button"
              title="Undo — restore what was in the subject and body (Ctrl+Z)"
              disabled={!bodyUndo.canUndo}
              onClick={bodyUndo.undo}
            >
              <UndoIcon />
            </TBtn>
            <ToolbarSpacer />
            {imageBusy && <Muted>Uploading…</Muted>}
            {imageError && <ErrText>{imageError}</ErrText>}
            <VarButton
              type="button"
              $inToolbar
              onClick={() => setVarMenuFor(varMenuFor === 'body' ? null : 'body')}
            >
              Insert variable <Caret>▾</Caret>
              {varMenuFor === 'body' && (
                <VarMenu $right $up onClick={e => e.stopPropagation()}>
                  {CAMPAIGN_TEMPLATE_VARIABLES.map(v => (
                    <VarItem key={v.key} type="button" onClick={() => insertVariable(v.key, 'body')}>
                      {v.token}
                    </VarItem>
                  ))}

                  {/*
                    Body only. The signature is a block of markup — in a subject line it
                    would arrive as a wall of tags.

                    Placing it is optional: without the token the signature still lands
                    at the end of the email, as it always has. The token only decides
                    WHERE, which is why it is disabled rather than hidden when there is
                    no signature to place.
                  */}
                  <VarSep />
                  {hasAnySignature ? (
                    <VarItem type="button" onClick={() => insertSignature()}>
                      {'{{signature}}'}
                    </VarItem>
                  ) : (
                    <InfoTip
                      align="right"
                      text="Open Settings → Sending accounts to add a signature."
                    >
                      <VarItemOff aria-disabled="true">{'{{signature}}'}</VarItemOff>
                    </InfoTip>
                  )}
                </VarMenu>
              )}
            </VarButton>
          </Toolbar>
        </EditorShell>

        <input
          ref={fileRef}
          type="file"
          accept={IMAGE_TYPES}
          style={{ display: 'none' }}
          onChange={e => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) pickImage(file);
          }}
        />

        <ActionsWrap>
          <CampaignComposeActions
            generateDisabled={!bodyPlainText.trim()}
            onGenerate={() => setGenerateFor('body')}
            onInsertTemplate={content => bodyUndo.replace({ subject: subjectValue, body: content })}
          />
        </ActionsWrap>

        {/* The "Appended automatically" block is gone. What it explained — that a
            signature lands at the end — is now something the author places with
            {{signature}}, and the postal address it also hosted moved to Settings,
            beside the signature it prints under. */}

      </Field>

      <CampaignVariableWarnBanner tokens={badTokens} />

      {usedVariables.length > 0 && (
        <UsedRow>
          <UsedLabel>Fallback values:</UsedLabel>
          {usedVariables.map(key => {
            const has = !!(fallbacks[key] ?? '').trim();
            return (
              <UsedPill
                key={key}
                type="button"
                $missing={!has}
                onClick={e => setPopover({
                  key,
                  anchorRect: e.currentTarget.getBoundingClientRect(),
                  pendingInsert: false,
                })}
              >
                {variableToken(key)}{has && <FallbackHint>→ {fallbacks[key]}</FallbackHint>}
              </UsedPill>
            );
          })}
        </UsedRow>
      )}

      {/* Dropdowns rather than cards: these are two settings with short, mutually
          exclusive answers, and as cards they took more vertical space than the
          message they belong to. */}
      {!lockDeliveryOptions && (
      <OptionRow>
        <OptionGroup>
          <OptionLabel>Email address</OptionLabel>
          <OptionSelect
            value={step.emailPreference ?? 'business'}
            onChange={e => onChange({ emailPreference: e.target.value as EmailPreference })}
          >
            {EMAIL_PREFERENCE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label} · {opt.cost}</option>
            ))}
          </OptionSelect>
        </OptionGroup>

        <OptionGroup>
          <OptionLabel>If no email address</OptionLabel>
          <OptionSelect
            value={step.ifNoEmail ?? 'skip'}
            onChange={e => onChange({ ifNoEmail: e.target.value as IfNoEmail })}
          >
            {IF_NO_EMAIL_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </OptionSelect>
        </OptionGroup>
      </OptionRow>
      )}

      {popover && (
        <VariableFallbackPopover
          variableKey={popover.key}
          value={fallbacks[popover.key] ?? ''}
          anchorRect={popover.anchorRect}
          mode="edit"
          onSave={(val, asDefault) => { onFallbackChange(popover.key, val, asDefault); setPopover(null); }}
          onClose={() => setPopover(null)}
        />
      )}

      {/*
        One dialog for both fields — which one is open decides what it is given and
        where the accepted draft lands. The body is handed plain text and comes back
        as plain text, so it is converted to markup on the way in.
      */}
      <CampaignGeneratePopup
        show={generateFor !== null}
        mode={generateFor === 'preview' ? 'preview' : 'generate'}
        onHide={() => setGenerateFor(null)}
        stepType="email"
        instruction={bodyPlainText}
        language={step.aiLanguage || DEFAULT_AI_LANGUAGE}
        campaignDescription={campaignDescription}
        variableFallbacks={fallbacks}
        /* The first sender stands in for the rest. With several, the preview shows
           whose signature it is rather than implying there is only one. */
        senderName={previewSender?.name}
        senderSubline={previewSender?.subline}
        signatureHtml={previewSender?.signature}
        subject={subjectValue}
        /* Subject and body arrive together. The subject only overwrites the field when
           one actually came back — a failed subject call must not blank a good one. */
        /* One replace for both, so one Undo puts both back. Writing the subject
           separately afterwards would leave it outside the snapshot. */
        onUse={(text, generatedSubject) => bodyUndo.replace({
          subject: generatedSubject || subjectValue,
          body: textToHtml(text),
        })}
      />

      {/*
        Settings over the campaign, not instead of it — the signature {{signature}}
        resolves to is written there, and sending the author away mid-draft to add one
        loses their place.
      */}
      <StyledModal
        show={settingsOpen}
        onHide={() => setSettingsOpen(false)}
        width="min(1040px, 94vw)"
        backdrop
      >
        <SettingsHead>
          <SettingsTitle>Sending accounts</SettingsTitle>
          <SettingsClose type="button" onClick={() => setSettingsOpen(false)} aria-label="Close">
            <CloseIcon />
          </SettingsClose>
        </SettingsHead>
        <SettingsBody>
          <SendingAccountsSection inDialog />
        </SettingsBody>
      </StyledModal>

      <LinkUrlDialog show={linkOpen} onHide={() => setLinkOpen(false)} onSubmit={applyLink} />

      {/* Any click outside closes the variable menu. */}
      {varMenuFor && <MenuScrim onClick={() => setVarMenuFor(null)} />}
    </Wrap>
  );
};

export default CampaignEmailComposer;

// ─── Plain text ⇄ markup ──────────────────────────────────────────────────────

/**
 * The AI writes plain text; the body is stored as markup. Escaping first matters —
 * a model that returns "R&D" or "<3" would otherwise inject broken tags into the
 * email, and the server's sanitizer would then strip whatever it made of them.
 */
function textToHtml(text: string): string {
  const escaped = String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  if (!escaped.trim()) return '';
  return escaped
    .split(/\n{2,}/)
    .map(block => `<div>${block.replace(/\n/g, '<br>')}</div>`)
    .join('<div><br></div>');
}

/** The reverse, for handing the current body back to the model as an instruction. */
function htmlToText(html: string): string {
  return String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(div|p|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// ─── Icons ────────────────────────────────────────────────────────────────────
// Written inline rather than pulled from SvgFragment: these are toolbar glyphs at a
// specific weight, and the shared set is built for larger, filled step icons.

const svg = (d: React.ReactNode, size = 16) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    // `display:block` kills the inline-baseline gap that made these sit low against
    // the text beside them.
    style={{ display: 'block' }}>{d}</svg>
);

const CloseIcon = () => svg(<><path d="M18 6L6 18" /><path d="M6 6l12 12" /></>);
const ListIcon = () => svg(<><path d="M8 6h13M8 12h13M8 18h13" /><circle cx="3.5" cy="6" r="1" /><circle cx="3.5" cy="12" r="1" /><circle cx="3.5" cy="18" r="1" /></>);
const OrderedListIcon = () => svg(<><path d="M10 6h11M10 12h11M10 18h11" /><path d="M4 4h1v4M3 12h2.5L3 15.5h2.5M3 18h2.5v2H3v-2" /></>);
const LinkIcon = () => svg(<><path d="M10 13a5 5 0 007.5.5l3-3a5 5 0 00-7-7l-1.7 1.7" /><path d="M14 11a5 5 0 00-7.5-.5l-3 3a5 5 0 007 7l1.7-1.7" /></>);
const ImageIcon = () => svg(<><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></>);
const ClearIcon = () => svg(<><path d="M4 7h16" /><path d="M10 7V5h4v2" /><path d="M6 7l1 13h10l1-13" /></>);
// ─── Styles ───────────────────────────────────────────────────────────────────

const ACCENT = styles.colors.primary600;

const Wrap = styled.div`
  display: flex; flex-direction: column; gap: 14px; width: 100%;
`;


const ReqMark = styled.span`color: ${ACCENT};`;

const FieldHeadSpacer = styled.div`flex: 1 1 auto;`;


/* Sits inside the subject input's right edge, as in the design. Centred with top/bottom
   and auto margins rather than translateY: a transform opens a stacking context, which
   is what buried the variable menu under the click-catcher. */
const InputVarBtn = styled.button`
  position: absolute; right: 6px; top: 0; bottom: 0; margin: auto 0;
  display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 26px; border-radius: 6px;
  border: none; background: none; cursor: pointer;
  font-family: 'SF Mono', Menlo, monospace; font-size: 12px; color: #64748b;
  &:hover { background: #f1f5f9; color: ${ACCENT}; }
`;

const ActionsWrap = styled.div`
  margin-top: 2px;
`;

const Field = styled.div`
  display: flex; flex-direction: column; gap: 6px; width: 100%;
`;

const FieldHead = styled.div`
  display: flex; align-items: center; justify-content: space-between;
`;

const FieldLabel = styled.span`
  font-family: NunitoSans; font-size: 12px; font-weight: 700; color: #334155;
`;

const InputRow = styled.div`
  position: relative;
  display: flex; align-items: stretch; width: 100%;
`;

const SubjectInput = styled.input<{ $invalid?: boolean }>`
  flex: 1 1 auto; min-width: 0; box-sizing: border-box;
  border: 1.5px solid ${p => (p.$invalid ? '#fca5a5' : '#e2e8f0')};
  border-radius: 10px; padding: 11px 38px 11px 13px; background: #fff;
  font-family: NunitoSans; font-size: 13px; color: #0f172a;
  &:focus { outline: none; border-color: ${ACCENT}; }
  &::placeholder { color: #b6c0cd; }
`;

const Caret = styled.span`font-size: 9px; margin-left: 2px;`;

const VarButton = styled.button<{ $inToolbar?: boolean }>`
  position: relative;
  flex: 0 0 auto;
  display: inline-flex; align-items: center; gap: 4px;
  border: ${p => (p.$inToolbar ? 'none' : '1px solid #e2e8f0')};
  background: ${p => (p.$inToolbar ? 'transparent' : '#fff')};
  border-radius: 8px; padding: ${p => (p.$inToolbar ? '4px 6px' : '9px 11px')};
  cursor: pointer;
  font-family: NunitoSans; font-size: 12px; font-weight: 600; color: #475569;
  &:hover { color: ${ACCENT}; }
`;

/*
 * `$up` opens the menu above the button instead of below it.
 *
 * The body toolbar sits at the BOTTOM of the editor, which is itself at the bottom of
 * the step card — and StepCard is `overflow: hidden` for its rounded corners. A menu
 * dropping downward from there is cut off at the card's edge, which is why the last
 * entry ({{signature}}) was half-visible and hard to click. Opening upward puts the
 * menu over the editable area, where there is always room. The subject's menu is near
 * the top of the card and stays downward.
 */
const VarMenu = styled.div<{ $right?: boolean; $up?: boolean }>`
  position: absolute; z-index: 30;
  ${p => (p.$up ? 'bottom: calc(100% + 4px);' : 'top: calc(100% + 4px);')}
  ${p => (p.$right ? 'right: 0;' : 'left: 0;')}
  min-width: 150px; padding: 4px;
  background: #fff; border: 1px solid #e2e8f0; border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  display: flex; flex-direction: column;
`;

const VarItem = styled.button`
  border: none; background: none; cursor: pointer; text-align: left;
  padding: 6px 8px; border-radius: 6px;
  font-family: NunitoSans; font-size: 12px; color: #0f172a;
  &:hover { background: #f3f0ff; color: ${ACCENT}; }
`;

const SettingsHead = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 16px 20px 12px; border-bottom: 1px solid #eef1f6;
`;

const SettingsTitle = styled.div`
  font-family: NunitoSans; font-size: 16px; font-weight: 800; color: #0f172a;
`;

const SettingsClose = styled.button`
  background: none; border: none; padding: 2px; cursor: pointer;
  color: #94a3b8;
  &:hover { color: #475569; }
`;

/* The settings page brings its own padding; cap the height so a long account list
   scrolls inside the dialog instead of pushing it off screen. */
const SettingsBody = styled.div`
  max-height: 72vh; overflow-y: auto;
`;
const VarSep = styled.div`
  height: 1px; background: #eef1f6; margin: 4px 2px;
`;

/* A div, not a disabled button: a disabled button swallows pointer events in every
   browser, and the tooltip explaining why it is unavailable would never appear. */
const VarItemOff = styled.div`
  padding: 6px 8px; border-radius: 6px; cursor: default;
  font-family: NunitoSans; font-size: 12px; color: #b6c0cd;
`;

/* Catches the click that dismisses an open variable menu. */
const MenuScrim = styled.div`
  position: fixed; inset: 0; z-index: 20;
`;

const PreviewLine = styled.div`
  font-family: NunitoSans; font-size: 11px; color: #94a3b8;
`;

const EditorShell = styled.div<{ $invalid?: boolean }>`
  border: 1.5px solid ${p => (p.$invalid ? '#fca5a5' : '#e2e8f0')};
  border-radius: 10px; background: #fff; overflow: visible;
  &:focus-within { border-color: ${ACCENT}; }
`;

/* Under the editor, not above it — see the render. */
const Toolbar = styled.div`
  display: flex; align-items: center; gap: 2px; flex-wrap: wrap;
  padding: 6px 8px; border-top: 1px solid #eef1f6;
`;

const ToolbarSpacer = styled.div`flex: 1 1 auto;`;

/* Dark enough to actually read on the toolbar's white ground — at #475569 with a
   1.5px stroke these washed out to the point of being invisible in a screenshot. */
const TBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border: none; background: none; cursor: pointer;
  border-radius: 6px; color: #1e293b;
  font-family: NunitoSans; font-size: 14px;
  &:hover:not(:disabled) { background: #eef1f6; color: ${ACCENT}; }
  &:disabled { opacity: 0.45; cursor: default; }
`;

const TSep = styled.div`
  width: 1px; height: 18px; background: #e2e8f0; margin: 0 4px;
`;

const Editable = styled.div<{ $empty?: boolean }>`
  min-height: 190px; max-height: 460px; overflow-y: auto;
  padding: 12px 14px; box-sizing: border-box;
  font-family: Arial, Helvetica, sans-serif; font-size: 13px; line-height: 1.6;
  color: #0f172a;
  &:focus { outline: none; }
  img { max-width: 100%; height: auto; }
  a { color: ${ACCENT}; }
  ul, ol { padding-left: 22px; margin: 6px 0; }

  ${p => p.$empty && `
    &::before {
      content: attr(data-placeholder);
      color: #b6c0cd;
      font-family: NunitoSans;
      pointer-events: none;
    }
  `}
`;

const Chars = styled.span`
  font-family: NunitoSans; font-size: 11px; color: #94a3b8;
`;

const Muted = styled.span`
  font-family: NunitoSans; font-size: 11px; color: #94a3b8;
`;

const ErrText = styled.span`
  font-family: NunitoSans; font-size: 11px; color: #dc2626;
`;

/* Shows what will actually be tacked on, rather than describing it. */




/* Matches the signature section's chevron: an SVG, because the "▸" glyph renders
   tiny and at a different weight in every font. */





/* The signature as the recipient sees it, scaled down — not the app's own type. */



/* A quiet button rather than an underlined link: it opens an editor on this page,
   it does not navigate anywhere. */
/* A real link now that it navigates to Settings — it used to scroll to a section
   further down this same page. */


const UsedRow = styled.div`
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
`;

const UsedLabel = styled.span`
  font-family: NunitoSans; font-size: 11px; color: #64748b;
`;

const UsedPill = styled.button<{ $missing?: boolean }>`
  display: inline-flex; align-items: center; gap: 4px;
  border: 1px solid ${p => (p.$missing ? '#fbbf24' : '#e2e8f0')};
  background: ${p => (p.$missing ? '#fffbeb' : '#f8fafc')};
  border-radius: 999px; padding: 3px 9px; cursor: pointer;
  font-family: NunitoSans; font-size: 11px; color: #475569;
`;

const FallbackHint = styled.span`color: #94a3b8;`;

const OptionRow = styled.div`
  display: flex; gap: 12px; width: 100%;
  ${campaignMobile`flex-direction: column;`}
`;

const OptionGroup = styled.div`
  flex: 1 1 0; min-width: 0;
  display: flex; flex-direction: column; gap: 5px;
`;

const OptionLabel = styled.span`
  font-family: NunitoSans; font-size: 12px; font-weight: 700; color: #334155;
`;

const OptionSelect = styled.select`
  ${campaignSelectStyle}
`;


